import type { AuditRow } from '@/lib/engagement-api';

/**
 * The audit log in words HR uses. The API stores machine names (`assessment.stage_submitted`)
 * and summaries with enum codes in them (`EMPLOYEE -> MANAGER`); both are translated here at
 * display time, so rows already written read as well as new ones.
 */
const ACTION_LABEL: Record<string, string> = {
  'employee.created': 'Added a new employee',
  'employee.updated': 'Updated employee details',
  'employee.role_changed': 'Changed access level',
  'employee.status_changed': 'Changed employment status',
  'employee.manager_changed': 'Changed reporting manager',
  'employee.password_set': 'Set a password',
  'employee.password_changed': 'Changed their password',
  'goals.published': 'Published goals',
  'goals.changes_requested': 'Asked for goals to be changed',
  'goals.sent': 'Sent goals',
  'goals.accepted': 'Accepted goals',
  'goals.rejected': 'Rejected goals',
  'assessment.submitted': 'Submitted an assessment',
  'assessment.stage_submitted': 'Submitted a review',
  'assessment.final_rating': 'Gave the final rating',
  'assessment.returned': 'Sent an assessment back for correction',
  'assessment.reopened': 'Reopened an assessment',
  'assessment.rating_accepted': "Accepted their manager's rating",
  'assessment.rating_declined': "Declined their manager's rating",
  'annual.finalised': 'Finalised the annual review',
  'cycle.targets_set': 'Set the rating distribution targets',
  'cycle.renamed': 'Renamed a performance cycle',
  'cycle.status_overridden': 'Manually changed a cycle status',
  'calibration.opened': 'Started calibration',
  'calibration.moved': 'Changed a grade in calibration',
  'calibration.locked': 'Locked calibration',
  'calibration.unlocked': 'Reopened calibration',
  'quarter.window_forced': 'Manually opened or closed a review window',
  'quarter.window_configured': 'Changed review window dates',
  'quarter.review_chain': 'Changed who reviews this quarter',
  'quarter.frozen': 'Froze a quarter',
  'quarter.unfrozen': 'Unfroze a quarter',
  'kra_group.assigned': 'Added KRAs to a goal',
  'kra_set.created': 'Created a KRA group',
  'kra_set.updated': 'Edited a KRA group',
  'kra_set.deleted': 'Deleted a KRA group',
  'kra_set.assigned': 'Assigned goals',
  'kra_set.unassigned': 'Removed assigned goals',
  'goal.added': 'Added a goal',
  'goal.updated': 'Edited a goal',
  'goal.deleted': 'Deleted a goal',
  'kra.added': 'Added a KRA',
  'kra.deleted': 'Deleted a KRA',
  'kra.archived': 'Archived a KRA',
  'kra.unarchived': 'Restored a KRA',
  'kra.seeded_from_designations': 'Created KRAs from job titles',
  'org.updated': 'Updated the organization',
  'org.deleted': 'Deleted from the organization',
  'one_on_one.not_held': 'Marked a 1-on-1 as not held',
  'pip.started': 'Started an improvement plan',
  'pip.closed': 'Closed an improvement plan',
  'attachment.uploaded': 'Uploaded a file',
  'attachment.deleted': 'Deleted a file',
  'dev_connect.session_created': 'Opened a Dev Connect session',
  'dev_connect.session_updated': 'Edited a Dev Connect session',
  'dev_connect.held': 'Recorded a Dev Connect session',
  'dev_connect.slot_released': 'Released a Dev Connect slot',
};

/** `assessment.stage_submitted` summaries start with the stage: "MANAGER submitted · weighted 7.2". */
const STAGE_SUBMITTED: Record<string, string> = {
  SELF: 'Submitted their self-assessment',
  MANAGER: 'Submitted the manager review',
  HR: 'Submitted the HR review',
  MANAGEMENT: 'Submitted the management review',
};

/** Roles, stages and statuses as the API writes them into summaries. */
const CODE_WORDS: Record<string, string> = {
  SELF: 'Self',
  EMPLOYEE: 'Employee',
  MANAGER: 'Manager',
  MANAGEMENT: 'Management',
  SUPER_ADMIN: 'Super admin',
  DEPT_HEAD: 'Department head',
  SELF_IN_PROGRESS: 'Self-assessment in progress',
  AWAITING_MANAGER: 'Waiting for manager',
  AWAITING_EMPLOYEE_ACCEPTANCE: 'Waiting for employee to accept',
  AWAITING_HR: 'Waiting for HR',
  AWAITING_MANAGEMENT: 'Waiting for management',
  ON_PIP: 'On improvement plan',
  OPEN: 'open',
  CLOSED: 'closed',
  ACTIVE: 'Active',
  EXITED: 'Exited',
  UPCOMING: 'Upcoming',
  COMPLETED: 'Completed',
};

const sentence = (s: string) => s.charAt(0).toUpperCase() + s.slice(1).toLowerCase();

export const formatWhen = (iso: string) =>
  new Date(iso).toLocaleString('en-IN', {
    day: 'numeric',
    month: 'short',
    year: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });

/** Enum codes to words, ISO timestamps to dates, `->` to an arrow. User-typed text is left alone. */
export function humanizeSummary(summary: string): string {
  return summary
    .replace(/\d{4}-\d{2}-\d{2}T[\d:.]+Z/g, (iso) => formatWhen(iso))
    .replace(/\s->\s/g, ' → ')
    .replace(/\b[A-Z]+(?:_[A-Z]+)+\b|\b[A-Z]{4,}\b/g, (code) =>
      CODE_WORDS[code] ?? (code.includes('_') ? sentence(code.replace(/_/g, ' ')) : code),
    );
}

export function describeAudit(row: Pick<AuditRow, 'action' | 'summary'>): {
  label: string;
  details: string | null;
} {
  if (row.action === 'assessment.stage_submitted' && row.summary) {
    const stage = row.summary.split(' ')[0] ?? '';
    const score = row.summary.match(/weighted ([\d.]+)/)?.[1];
    return {
      label: STAGE_SUBMITTED[stage] ?? 'Submitted a review',
      details: score ? `Weighted score ${score}` : null,
    };
  }
  return {
    label: ACTION_LABEL[row.action] ?? sentence(row.action.replace(/[._]/g, ' ')),
    details: row.summary ? humanizeSummary(row.summary) : null,
  };
}

/** The screen's search box: who did it, whose record, or what happened, in the words shown. */
export function matchesAudit(row: AuditRow, query: string): boolean {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const { label, details } = describeAudit(row);
  return [row.actor?.name, row.actor?.email, row.subject, label, details].some((s) =>
    s?.toLowerCase().includes(q),
  );
}

/** The screen's filter, as action prefixes the API matches with startsWith. */
export const AUDIT_CATEGORIES = [
  { label: 'Reviews & ratings', prefixes: ['assessment.', 'annual.', 'calibration.'] },
  { label: 'Goals & KRAs', prefixes: ['goals.', 'goal.', 'kra.', 'kra_set.', 'kra_group.'] },
  { label: 'People & roles', prefixes: ['employee.'] },
  { label: 'Cycles & quarters', prefixes: ['cycle.', 'quarter.'] },
  { label: 'Improvement plans', prefixes: ['pip.'] },
  { label: '1-on-1s', prefixes: ['one_on_one.'] },
  { label: 'Files', prefixes: ['attachment.'] },
  { label: 'Organization', prefixes: ['org.'] },
  { label: 'Dev Connect', prefixes: ['dev_connect.'] },
] as const;
